import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import { BLOG } from "../lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const more = BLOG.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <article data-testid="blog-post">
      {/* HERO */}
      <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${post.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/90 to-[#0a0a0a]" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-[#d4af37] transition-colors"
          >
            <ArrowLeft size={14} /> All Articles
          </Link>
          <div className="mt-8 flex items-center gap-4">
            <span className="section-label">{post.category}</span>
            <span className="font-sans text-xs text-white/45">{post.date} · {post.readTime}</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl lg:text-5xl xl:text-6xl text-white font-light leading-[1.05]">
            {post.title}
          </h1>
        </div>
      </section>

      {/* BODY */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden aspect-[16/9] hairline mb-12">
            <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="space-y-7">
            {post.content.map((para, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className="font-sans text-lg text-white/75 leading-relaxed font-light">
                  {para}
                </p>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 hairline p-8 text-center">
            <SectionLabel testId="blog-cta-label">
              <span className="block text-center w-full">Ready to Begin?</span>
            </SectionLabel>
            <h3 className="mt-5 font-serif text-2xl lg:text-3xl text-white font-light">
              Help your child find their voice.
            </h3>
            <Link
              to="/admissions"
              data-testid="blog-post-cta"
              className="mt-7 inline-flex btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-8 py-4 items-center gap-3"
            >
              Apply Now <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* MORE */}
      <section className="py-20 bg-[#080808] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionLabel>Continue Reading</SectionLabel>
          <div className="mt-10 grid md:grid-cols-3 gap-6 lg:gap-8">
            {more.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-more-${p.slug}`} className="lux-card group block h-full">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-[1.2s]" />
                  </div>
                  <div className="p-6">
                    <span className="section-label">{p.category}</span>
                    <h4 className="mt-3 font-serif text-xl text-white font-light leading-snug">{p.title}</h4>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
